import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bookmark, Download } from 'lucide-react';
import { Botao, Indicador } from '../../componentes/base';
import { AvisoExportacao } from '../../componentes/discreto';
import { Alternador } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { anunciarOcultos, useDiscreto } from '../../componentes/sensivel';
import { SeletorPeriodo } from '../../componentes/periodo/SeletorPeriodo';
import { buscaComPeriodo, escolhaDoPeriodo, hojeNoFuso, periodoDaEscolha, resolverPeriodo, type PeriodoGuardado } from '../../componentes/periodo/periodo';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { ModalSalvo } from './salvos';
import type { Escopo } from './tipos';

/**
 * Parâmetros do relatório na URL (link compartilhável): o período (`de`, `ate`) e os filtros da tela.
 * Review r29 robustez (B2, varredura): só vale o valor aceito (`aceitos`: a lista ou o teste); fora disso (`?base=xx`,
 * `?conta_id=lixo`) fica o padrão, como nas listas. Antes ia cru à API e o relatório caía no erro (422).
 */
export function useParametro<T extends string>(chave: string, padrao: T, aceitos: readonly T[] | ((v: string) => boolean)): [T, (v: T) => void] {
  const [busca, setBusca] = useSearchParams();
  const bruto = busca.get(chave);
  const aceito = bruto !== null && (typeof aceitos === 'function' ? aceitos(bruto) : (aceitos as readonly string[]).includes(bruto));
  const valor = aceito ? (bruto as T) : padrao;
  const mudar = useCallback(
    (v: T) =>
      setBusca(
        (atual) => {
          const n = new URLSearchParams(atual);
          if (v === padrao) n.delete(chave);
          else n.set(chave, v);
          return n;
        },
        { replace: true },
      ),
    [chave, padrao, setBusca],
  );
  return [valor, mudar];
}

/** Período do relatório: o da URL, senão o mês atual (fuso do workspace). Atalho relativo, como no Início. */
export function usePeriodo() {
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const hoje = hojeNoFuso(s.workspace.fuso);
  const periodo = useMemo(() => resolverPeriodo({ de: busca.get('de'), ate: busca.get('ate') }, null, hoje), [busca, hoje]);
  const escolha = escolhaDoPeriodo(periodo, hoje);
  const aoEscolher = useCallback(
    (e: PeriodoGuardado) => {
      const p = periodoDaEscolha(e, hoje);
      setBusca((atual) => buscaComPeriodo(atual, p), { replace: true });
    },
    [hoje, setBusca],
  );
  return { periodo, escolha, aoEscolher, hoje, seletor: <SeletorPeriodo periodo={periodo} escolha={escolha} aoEscolher={aoEscolher} /> };
}

/** O `Segmentado` foi promovido ao núcleo (C1); segue exportado daqui para os relatórios. */
export { Segmentado } from '../../componentes/base';

/** Barra de filtros do relatório: quebra de linha no celular, sem rolagem lateral. */
export function Barra({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-2">{children}</div>;
}

type CorKpi = 'text-tinta-1' | 'text-ciano' | 'text-laranja' | 'text-verde' | 'text-perigo' | 'text-dourado';

/**
 * Indicador do relatório. Review r16 visual B3: é o `Indicador` do núcleo (o mesmo do Início e de Contas, com o tamanho do
 * valor pela largura do cartão), não um cartão próprio com `text-xl` fixo.
 */
export function Kpi({ rotulo, valor, cor = 'text-tinta-1', detalhe }: { rotulo: string; valor: string; cor?: CorKpi; detalhe?: ReactNode }) {
  return <Indicador rotulo={rotulo} valor={valor} detalhe={detalhe} cor={cor.slice(5) as 'tinta-1' | 'ciano' | 'laranja' | 'verde' | 'perigo' | 'dourado'} />;
}

/** Dinheiro do relatório: passa pelo modo discreto (formato.ts) e anuncia o oculto ao leitor de tela. */
export function Valor({ centavos, moeda, className = '' }: { centavos: number; moeda: string; className?: string }) {
  useDiscreto();
  const cor = centavos < 0 ? 'text-perigo' : '';
  return <span className={`numero whitespace-nowrap ${cor} ${className}`}>{anunciarOcultos(dinheiro(centavos, moeda))}</span>;
}

/** Percentual do relatório com 2 casas pelo `numero` do núcleo (review r16 visual B11: antes refazia o `toLocaleString`). */
export const percentualTexto = (v: string | null | undefined) => (v === null || v === undefined ? '' : `${numero(Number(v), 2)}%`);

/** Nome de parceiro no relatório: pelo modo discreto (iniciais com a opção de nomes). */
export function NomeParceiro({ nome, vazio }: { nome: string | null; vazio: string }) {
  useDiscreto();
  return <>{nome ? nomePessoa(nome) : vazio}</>;
}

/** Linha de escopo: empresa do relatório ou o consolidado (que segue o seletor de empresa da barra superior). */
export function LinhaEscopo({ escopo, extra }: { escopo: Escopo; extra?: ReactNode }) {
  const { t } = useTranslation('relatorios');
  const nome = escopo.empresas.find((e) => e.id === escopo.empresa_id)?.nome ?? '';
  return (
    <p className="text-sm text-tinta-3">
      {escopo.modo === 'consolidado' ? t('comum.escopoConsolidado', { count: escopo.empresas.length }) : t('comum.escopoEmpresa', { nome })}
      {extra}
    </p>
  );
}

/**
 * Exporta o relatório com os parâmetros da tela (CSV completo, por job) e leva às Exportações. E1 (resposta 5 do Gustavo):
 * completa por padrão, com a opção de sair mascarada (valores, percentuais e documento como ***), já ligada quando o modo
 * discreto está ativo. Ao lado, "Salvar" guarda o relatório com estes filtros em "Meus relatórios" (com `relatorios.salvar`).
 */
export function BotaoExportar({ relatorio, parametros }: { relatorio: string; parametros: Record<string, unknown> }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const { ativo } = useDiscreto();
  const [aberto, setAberto] = useState(false);
  const [salvar, setSalvar] = useState(false);
  const [mascarar, setMascarar] = useState(false);
  const limpos = Object.fromEntries(Object.entries(parametros).filter(([, v]) => v !== undefined && v !== ''));
  const m = useMutation({
    mutationFn: () => post<{ id: string; job_id: string }>('/relatorios/exportacoes', { relatorio, parametros: limpos, mascarar }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['/relatorios/exportacoes'] });
      toast.sucesso(t('comum.exportacaoNaFila'));
      navegar('/relatorios/relatorios/exportacoes');
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const podeExportar = s.pode('relatorios.exportar');
  const podeSalvar = s.pode('relatorios.salvar');
  if (!podeExportar && !podeSalvar) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {podeSalvar && (
        <Botao variante="secundario" icone={<Bookmark className="size-4" aria-hidden />} onClick={() => setSalvar(true)}>
          {t('salvos.salvar')}
        </Botao>
      )}
      {podeExportar && (
        <Botao
          icone={<Download className="size-4" aria-hidden />}
          onClick={() => {
            setMascarar(ativo);
            setAberto(true);
          }}
        >
          {t('comum.exportar')}
        </Botao>
      )}
      {salvar && <ModalSalvo relatorio={relatorio} parametros={limpos} aoFechar={() => setSalvar(false)} />}
      <Modal
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo={t('comum.exportar')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={m.isPending} onClick={() => m.mutate()}>
              {t('comum.exportar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {!mascarar && <AvisoExportacao />}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-tinta-1">{t('salvos.mascarar')}</p>
              <p className="text-xs text-tinta-3">{t('salvos.mascararAjuda')}</p>
            </div>
            <Alternador id={`exportar-mascarar-${relatorio}`} rotulo={t('salvos.mascarar')} marcado={mascarar} aoMudar={setMascarar} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Tabela com rolagem própria (focável pelo teclado), sem rolar a página para o lado. */
export function Rolagem({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div tabIndex={0} role="region" aria-label={rotulo} className="relative overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
      {children}
    </div>
  );
}

export const TH = 'px-4 py-2.5 font-medium whitespace-nowrap';
export const TD = 'px-4 py-2.5';
